import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Token management
const tokenStore = {
  get: () => localStorage.getItem('cl_access_token'),
  getRefresh: () => localStorage.getItem('cl_refresh_token'),
  set: (access, refresh) => {
    if (access) localStorage.setItem('cl_access_token', access);
    if (refresh) localStorage.setItem('cl_refresh_token', refresh);
  },
  clear: () => {
    localStorage.removeItem('cl_access_token');
    localStorage.removeItem('cl_refresh_token');
  }
};

// Attach Bearer token to every request
axios.interceptors.request.use(config => {
  const token = tokenStore.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 with token refresh (only for non-auth endpoints)
let refreshPromise = null;

axios.interceptors.response.use(
  res => res,
  async error => {
    const config = error.config;
    const url = config?.url || '';
    const isAuthUrl = url.includes('/auth/login') || url.includes('/auth/register') ||
                      url.includes('/auth/me') || url.includes('/auth/refresh') ||
                      url.includes('/auth/logout');

    if (error.response?.status === 401 && !config._retried && !isAuthUrl) {
      config._retried = true;
      const refreshToken = tokenStore.getRefresh();
      if (!refreshToken) return Promise.reject(error);

      // Deduplicate concurrent refresh calls
      if (!refreshPromise) {
        refreshPromise = axios.post(`${API}/api/auth/refresh`, { refresh_token: refreshToken })
          .then(res => {
            const newToken = res.data?.access_token;
            if (newToken) tokenStore.set(newToken, null);
            return newToken;
          })
          .catch(() => {
            tokenStore.clear();
            return null;
          })
          .finally(() => { refreshPromise = null; });
      }

      const newToken = await refreshPromise;
      if (newToken) {
        config.headers.Authorization = `Bearer ${newToken}`;
        return axios(config);
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

const formatApiError = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = tokenStore.get();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/api/auth/me`);
      setUser(response.data);
    } catch {
      tokenStore.clear();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/api/auth/login`, { email, password });
      const { access_token, refresh_token, ...userData } = response.data;
      tokenStore.set(access_token, refresh_token);
      setUser(userData);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiError(error.response?.data?.detail) || error.message };
    }
  };

  const register = async (email, password, name, role = 'researcher') => {
    try {
      const response = await axios.post(`${API}/api/auth/register`, { email, password, name, role });
      if (response.data.status === 'pending') return { success: true, pending: true };
      setUser(response.data);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiError(error.response?.data?.detail) || error.message };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/api/auth/logout`); } catch {}
    tokenStore.clear();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, isAuthenticated: !!user, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
